// CB:BEGIN contractor-action-required-panel
import React, { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { getApiUrl } from '../lib/getApiUrl';

interface ContractorActionRequiredPanelProps {
  onNavigate: (screen: string) => void;
}

interface ActionItem {
  projectId: string;
  address: string;
  action: string;
  screen: string;
  priority: 'high' | 'normal';
}

function getActionForProject(project: any): ActionItem | null {
  const status = String(project.status || '');
  const address = project.address || 'Your project';

  const startDateConfirmed = project.startDateStatus === 'CONFIRMED';

  if (status === 'ESCROW_PENDING' || status === 'HOMEOWNER_CONTRACT_SIGNING') {
    if (startDateConfirmed) return null;
    return { projectId: project.id, address, action: 'Confirm project start date', screen: 'escrow-dashboard', priority: 'high' };
  }
  if (status === 'ACTIVE_P1' || status === 'IN_PROGRESS') {
    return { projectId: project.id, address, action: 'Submit milestone evidence', screen: 'escrow-dashboard', priority: 'high' };
  }
  if (status === 'ACTIVE_P2') {
    return { projectId: project.id, address, action: 'Submit Phase 2 milestone evidence', screen: 'escrow-dashboard', priority: 'normal' };
  }
  if (status === 'ACTIVE_P3') {
    return { projectId: project.id, address, action: 'Submit completion notice', screen: 'escrow-dashboard', priority: 'normal' };
  }
  if (status === 'BID_SELECTED' || status === 'SCHEDULED') {
    if (startDateConfirmed) return null;
    return { projectId: project.id, address, action: 'Confirm project start date', screen: 'escrow-dashboard', priority: 'normal' };
  }
  return null;
}

const ContractorActionRequiredPanel: React.FC<ContractorActionRequiredPanelProps> = ({ onNavigate }) => {
  const { user } = useUser();
  const [actions, setActions] = useState<ActionItem[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        const res = await fetch(getApiUrl(`/api/contractors/active-projects?userId=${user.id}`), { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        const projects = data.projects || [];
        const items: ActionItem[] = [];
        for (const p of projects) {
          const item = getActionForProject(p);
          if (item) items.push(item);
        }
        setActions(items);
      } catch {
        // non-critical
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  if (!loaded || actions.length === 0) return null;

  return (
    <div
      style={{
        background: '#fef3c7',
        border: '1px solid #fde68a',
        borderRadius: 12,
        padding: 20,
        marginBottom: 20,
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 600, color: '#92400e', marginBottom: 12 }}>
        Action Required ({actions.length})
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {actions.map((item) => (
          <div
            key={item.projectId}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'white',
              borderRadius: 8,
              padding: '10px 14px',
              border: item.priority === 'high' ? '1px solid #f59e0b' : '1px solid #e5e7eb',
            }}
          >
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{item.action}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>{item.address}</div>
            </div>
            <button
              type="button"
              onClick={() => onNavigate(item.screen)}
              style={{
                background: '#f59e0b',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              Go →
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ContractorActionRequiredPanel;
// CB:END contractor-action-required-panel
